import React, { useState } from 'react';
import { Typography, Select, Checkbox } from 'antd';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import { MemberOptionLabel, useMemberSearch } from '../common/memberSearch';

interface BookingMemberSelectorProps {
  membershipId: string;
  onChange: (membershipId: string) => void;
  isAdmin: boolean;
  memberName: string;
  // When true, render only the full-width control (no inline "Booking Member:"
  // label), so a caller can place it inside its own vertical/labelled form.
  controlOnly?: boolean;
}

const BookingMemberSelector: React.FC<BookingMemberSelectorProps> = ({
  membershipId,
  onChange,
  isAdmin,
  memberName,
  controlOnly = false
}) => {
  // Default to active members only; unticking widens the search to everyone.
  const [activeOnly, setActiveOnly] = useState(true);
  const { results, search, reset } = useMemberSearch(activeOnly);

  // Keep the current selection visible even when it isn't in the latest search
  // results (the results list changes as the operator types).
  const selectedIsInResults = results.some(r => r.membershipId === membershipId);

  const handleActiveOnlyChange = (e: CheckboxChangeEvent) => {
    setActiveOnly(e.target.checked);
  };

  const control = isAdmin ? (
        <div style={{ display: "flex", flexDirection: "column", gap: "4px", width: controlOnly ? "100%" : undefined }}>
          <Select
            style={controlOnly ? { width: "100%" } : { minWidth: "200px" }}
            placeholder="Search by name, id, phone, legacy id or email"
            value={membershipId || undefined}
            onChange={onChange}
            showSearch
            // Matching + ranking happen server-side; don't re-filter or re-order.
            filterOption={false}
            onSearch={search}
            onBlur={reset}
            optionLabelProp="label"
            autoClearSearchValue={false}
            defaultActiveFirstOption
            notFoundContent={null}
          >
            {membershipId && !selectedIsInResults && (
              <Select.Option value={membershipId} label={memberName || membershipId}>
                {memberName || membershipId}
              </Select.Option>
            )}
            {results.map((member, index) => (
              <Select.Option
                key={`member-${member.membershipId}-${index}`}
                value={member.membershipId}
                label={member.name}
              >
                <MemberOptionLabel member={member} />
              </Select.Option>
            ))}
          </Select>
          <Checkbox
            checked={activeOnly}
            onChange={handleActiveOnlyChange}
            data-testid="booking-member-active-only"
          >
            Active only
          </Checkbox>
        </div>
      ) : (
        <Typography.Text>{memberName}</Typography.Text>
      );

  if (controlOnly) {
    return control;
  }

  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
      <Typography.Text><strong>Booking Member:</strong></Typography.Text>
      {control}
    </div>
  );
};

export default BookingMemberSelector;
